import TextField from '@atlaskit/textfield';
import AddressApiService from 'core/api/address/address-api.service';
import { useState } from 'react';
import { createAddressString } from 'core/utilities/address-string-builder';
import { useFlags } from '@atlaskit/flag';
import { showErrorFlag } from 'core/utilities/flags-helper';
import SharedButton from 'shared/components/buttons/button';
import ManualAddressForm from './manual-address-form';
import { IAddress, IAddressSearchSuggestion } from 'core/api/address/address-api-interface';
import { isNotNullOrEmpty } from 'core/utilities/null-checkers';

interface IAddressSearch {
  fieldProps: any;
}

const AddressSearch = ({ fieldProps }: IAddressSearch) => {
  const defaultAddress = fieldProps.value.fullAddress;
  const [suggestions, setSuggestions] = useState<IAddressSearchSuggestion[]>();
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState<string>(
    defaultAddress ? createAddressString([...defaultAddress.formatted_address, defaultAddress.postcode]) : '',
  );
  const [selectedAddress, setSelectedAddress] = useState<string | undefined>(
    defaultAddress ? createAddressString([...defaultAddress.formatted_address, defaultAddress.postcode]) : undefined,
  );
  const [manualEntry, setManualEntry] = useState(false);

  const flags = useFlags();
  const handleSelect = async (suggestion: IAddressSearchSuggestion) => {
    try {
      const { result } = await AddressApiService.getFullAddress(suggestion.id);
      let lat: number | undefined;
      let lng: number | undefined;
      if (isNotNullOrEmpty(result.latitude)) {
        lat = typeof result.latitude === 'string' ? parseFloat(result.latitude) : result.latitude;
      }
      if (isNotNullOrEmpty(result.longitude)) {
        lng = typeof result.longitude === 'string' ? parseFloat(result.longitude) : result.longitude;
      }
      if (!lat || !lng) {
        throw new Error('No latitude or longitude returned from address API');
      }
      setSelectedAddress(`${suggestion.address}, ${result.postcode}`);
      const fullAddress: IAddress = {
        postcode: result.postcode,
        latitude: lat,
        longitude: lng,
        formatted_address: [result.line_1, result.line_2, result.line_3, result.post_town, result.postcode].filter(
          (line) => isNotNullOrEmpty(line),
        ),
        thoroughfare: result.thoroughfare,
        building_name: result.building_name,
        sub_building_name: result.sub_building_name,
        sub_building_number: '',
        building_number: result.building_number ? parseInt(result.building_number) : 0,
        line_1: result.line_1,
        line_2: result.line_2,
        line_3: result.line_3,
        line_4: '',
        locality: result.dependant_locality,
        town_or_city: result.post_town,
        county: result.county,
        district: result.district,
        country: result.country,
        residential: true,
      };
      fieldProps.onChange({
        fullAddress,
        valid: true,
      });
      setSuggestions(undefined);
    } catch (error) {
      showErrorFlag(
        'An error occurred',
        'We failed to get the full details for this address, please try again.',
        flags,
      );
      console.error(error);
    }
  };

  const getSuggestions = async () => {
    if (value === '') {
      return;
    }
    setLoading(true);
    try {
      const result = await AddressApiService.autocomplete(value);
      setSuggestions(result);
      setLoading(false);
    } catch (error) {
      showErrorFlag(
        'An error occurred',
        'We could not retrieve the address suggestions for your query, please try again.',
        flags,
      );
      console.error(error);
      setLoading(false);
    }
  };

  const inputChanged = (value: string) => {
    setValue(value);
    setSuggestions(undefined);
  };

  const resetSearch = () => {
    setSelectedAddress(undefined);
    fieldProps.onChange({
      valid: false,
    });
  };

  return (
    <>
      <div className='flex items-center justify-between'>
        {!selectedAddress ? (
          <>
            <TextField
              {...fieldProps}
              isDisabled={fieldProps.isDisabled || manualEntry}
              value={value}
              onChange={(e) => inputChanged(e.currentTarget.value)}
            />
            <div className='ml-2'>
              <SharedButton
                isLoading={loading}
                onClick={() => getSuggestions()}
                type={undefined}
                appearance='primary'
                label='Search'
                disabled={manualEntry}
              />
            </div>
          </>
        ) : (
          <>
            <TextField isDisabled value={selectedAddress} onChange={() => {}} />
            <div className='ml-2'>
              <SharedButton
                isLoading={loading}
                onClick={() => resetSearch()}
                type={undefined}
                appearance='primary'
                label='Reset'
              />
            </div>
          </>
        )}
      </div>
      {suggestions && (
        <div className='border-2 rounded-sm overflow-hidden mt-2'>
          {loading ? (
            <div className='body-02 p-1.5'>Searching...</div>
          ) : (
            <div className='max-h-[200px] overflow-y-auto'>
              {suggestions.map((suggestion) => (
                <div
                  key={suggestion.id}
                  className='cursor-pointer hover:bg-gray-50 body-02 p-1.5 border-t-2 first:border-t-0'
                  onClick={() => handleSelect(suggestion)}
                >
                  {suggestion.address}
                </div>
              ))}
              {suggestions.length === 0 && <p className='body-02 p-1.5'>No results</p>}
            </div>
          )}
        </div>
      )}
      {selectedAddress === undefined && (
        <div className='mt-2'>
          <SharedButton
            onClick={() => {
              setManualEntry((prev) => {
                inputChanged('');
                setSuggestions(undefined);
                return !prev;
              });
            }}
            type={undefined}
            appearance='subtle'
            label={manualEntry ? 'Search for address' : 'Enter address manually'}
            spacing='compact'
          />
        </div>
      )}
      {manualEntry && selectedAddress === undefined && (
        <ManualAddressForm
          onChange={(value) => {
            if (!value) {
              return;
            }
            setSelectedAddress(`${value.detail.formatted_address.join(', ')}`);
            fieldProps.onChange({
              fullAddress: value.detail,
              valid: true,
            });
          }}
        />
      )}
    </>
  );
};

export default AddressSearch;
